import { useMemo } from 'react';

import {
  BoxIcon,
  MessageSquareIcon,
  ThumbsUpIcon,
  UserRoundIcon,
  BookmarkIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getIntegrationByType } from '@/components/organisms/Integrations';
import {
  SearchableMenuGroup,
  SearchableMenuItem,
} from '@/components/ui/custom/SearchableDropdownMenu/SearchableDropdownMenu';
import useBrains from '@/hooks/useBrains';
import useDesks from '@/hooks/useDesks';
import useMembers from '@/hooks/useMembers';
import { selectAvailableChannels } from '@/redux/integrations/selectors';
import { FilterViewListItem } from '@/shared/types/analytics';
import { IntegrationType } from '@/shared/types/integration';
import { getChannelKey } from '@/util/channelName';
import { FilterIconComponent as FilterIcon, FilterIconSpacer } from '@/util/filterFormatters';

export const useAccountFiltersData = (
  onFilterClick: (filterId: string) => void,
  savedViews?: FilterViewListItem[],
  onLoadView?: (viewId: string) => void,
  allowedFilters?: string[]
): { menuGroups: SearchableMenuGroup[] } => {
  const { t } = useTranslation();
  const availableChannels = useSelector(selectAvailableChannels);
  const { desks } = useDesks();
  const { brains } = useBrains();
  const { members } = useMembers();

  const savedViewMenuItems = useMemo((): SearchableMenuItem[] => {
    if (!savedViews || savedViews.length === 0 || !onLoadView) {
      return [];
    }

    return savedViews.map((view) => ({
      id: `view:${view.id}`,
      label: view.name,
      icon: null,
      onClick: () => onLoadView(view.id),
      searchText: view.name,
    }));
  }, [savedViews, onLoadView]);

  const channelMenuItems = useMemo((): SearchableMenuItem[] => {
    if (!availableChannels || availableChannels.length === 0) {
      return [];
    }

    return availableChannels.map((channel: IntegrationType) => {
      const localizedLabel = t(getChannelKey(channel));
      const Integration = getIntegrationByType(channel)?.Icon;

      const searchTokens: string[] = [channel];
      if (localizedLabel && typeof localizedLabel === 'string') {
        searchTokens.push(localizedLabel);
      }

      return {
        id: `channel:${channel}`,
        label: localizedLabel || channel,
        icon: Integration ? <Integration className="h-4 w-4 text-foreground-muted" /> : null,
        onClick: () => onFilterClick(`channel:${channel}`),
        searchText: searchTokens.join(' '),
        searchOnly: true,
      };
    });
  }, [availableChannels, onFilterClick, t]);

  const menuGroups: SearchableMenuGroup[] = useMemo(() => {
    // Helper function to check if a filter is allowed
    const isFilterAllowed = (filterId: string): boolean => {
      if (!allowedFilters || allowedFilters.length === 0) {
        return true;
      }
      // Check both the UI id and the data model type
      if (filterId === 'language-models') {
        return allowedFilters.includes('languageModelIds');
      }
      return allowedFilters.includes(filterId);
    };

    // Build search-only items for Environments (desks) and Brains for better discoverability
    const deskMenuItems: SearchableMenuItem[] = Array.isArray(desks)
      ? desks.map((desk) => ({
          id: `desk:${desk.desk_id}`,
          label: desk.name,
          icon: <BoxIcon className="h-4 w-4 text-foreground-muted" />,
          onClick: () => onFilterClick(`desk:${desk.desk_id}`),
          searchText: `${desk.name}`,
          searchOnly: true,
        }))
      : [];

    const brainMenuItems: SearchableMenuItem[] = Array.isArray(brains)
      ? brains.map((brain) => ({
          id: `brain:${brain.brain_id}`,
          label: brain.name,
          icon: <FilterIcon type="brainIds" />,
          onClick: () => onFilterClick(`brain:${brain.brain_id}`),
          searchText: `${brain.name}`,
          searchOnly: true,
        }))
      : [];

    const agentMenuItems: SearchableMenuItem[] = Array.isArray(members)
      ? members.map((member) => ({
          id: `agent:${member.user_id}`,
          label: member.name,
          icon: <UserRoundIcon className="h-4 w-4 text-foreground-muted" />,
          onClick: () => onFilterClick(`agent:${member.user_id}`),
          searchText: `${member.name}`,
          searchOnly: true,
        }))
      : [];

    // Group 1: Tags, Environments, Channels, AI Agents, Human agents, Knowledge used, Language models, First message type
    const allGroup1Items: SearchableMenuItem[] = [
      {
        id: 'tags',
        label: t('brain_logs.filters.groups.tags'),
        icon: <FilterIcon type="tags" />,
        onClick: () => onFilterClick('tags'),
      },
      {
        id: 'deskIds',
        label: t('brain_logs.filters.groups.environments'),
        icon: <FilterIcon type="environments" />,
        onClick: () => onFilterClick('deskIds'),
        children: deskMenuItems,
      },
      {
        id: 'channels',
        label: t('brain_logs.filters.groups.channels'),
        icon: <FilterIcon type="channels" />,
        onClick: () => onFilterClick('channels'),
        children: channelMenuItems,
      },
      {
        id: 'brainIds',
        label: t('common.ai-agents'),
        icon: <FilterIcon type="brainIds" />,
        onClick: () => onFilterClick('brainIds'),
        children: brainMenuItems,
      },
      {
        id: 'agentIds',
        label: t('analytics.human_agents'),
        icon: <UserRoundIcon className="h-4 w-4 text-foreground-muted" />,
        onClick: () => onFilterClick('agentIds'),
        children: agentMenuItems,
      },
      {
        id: 'hasCollection',
        label: t('brain_logs.filters.groups.collections_recalled'),
        icon: <FilterIcon type="hasCollection" />,
        onClick: () => onFilterClick('hasCollection'),
      },
      {
        id: 'language-models',
        label: t('brain_logs.filters.groups.language_models'),
        icon: <FilterIconSpacer />,
        onClick: () => onFilterClick('language-models'),
      },
      {
        id: 'firstMessageType',
        label: t('analytics.fields.first_message_type'),
        icon: <MessageSquareIcon className="h-4 w-4 text-foreground-muted" />,
        onClick: () => onFilterClick('firstMessageType'),
      },
    ];

    const group1Items = allGroup1Items.filter((item) => isFilterAllowed(item.id));

    // No extra search-only helper entries

    // Group 2 — Metrics (no Insights in Account dropdown)
    const allMetricsChildren: SearchableMenuItem[] = [
      {
        id: 'ratings',
        label: t('analytics.fields.csat'),
        section: t('brain_logs.filters.metric_groups.user_review'),
        onClick: () => onFilterClick('ratings'),
      },
      {
        id: 'reactions',
        label: t('analytics.fields.reactions'),
        section: t('brain_logs.filters.metric_groups.user_review'),
        onClick: () => onFilterClick('reactions'),
      },
      {
        id: 'csatBrain',
        label: t('brain_logs.filters.metrics.ai_csat'),
        section: t('brain_logs.filters.metric_groups.ai_review'),
        onClick: () => onFilterClick('csatBrain'),
      },
      {
        id: 'goalAchieved',
        label: t('brain_logs.filters.metrics.ai_agent_goal'),
        section: t('brain_logs.filters.metric_groups.ai_review'),
        onClick: () => onFilterClick('goalAchieved'),
      },
      {
        id: 'userSentiment',
        label: t('brain_logs.filters.metrics.user_sentiment'),
        section: t('brain_logs.filters.metric_groups.ai_review'),
        onClick: () => onFilterClick('userSentiment'),
      },
      {
        id: 'isContained',
        label: t('brain_logs.filters.metrics.containment'),
        section: t('brain_logs.filters.metric_groups.conversation'),
        onClick: () => onFilterClick('isContained'),
      },
      {
        id: 'isCovered',
        label: t('brain_logs.filters.metrics.coverage'),
        section: t('brain_logs.filters.metric_groups.conversation'),
        onClick: () => onFilterClick('isCovered'),
      },
      {
        id: 'isMeaningful',
        label: t('brain_logs.filters.metrics.meaningful'),
        section: t('brain_logs.filters.metric_groups.conversation'),
        onClick: () => onFilterClick('isMeaningful'),
      },
      {
        id: 'minNumUserMessages',
        label: t('brain_logs.filters.metrics.user_messages'),
        section: t('brain_logs.filters.metric_groups.conversation'),
        onClick: () => onFilterClick('minNumUserMessages'),
      },
    ];

    const filteredMetricsChildren = allMetricsChildren.filter((item) => isFilterAllowed(item.id));

    const metricsItems: SearchableMenuItem[] =
      filteredMetricsChildren.length > 0
        ? [
            {
              id: 'metrics',
              label: t('brain_logs.filters.groups.metrics'),
              icon: <ThumbsUpIcon className="h-4 w-4 text-foreground-muted" />,
              children: filteredMetricsChildren,
            },
          ]
        : [];

    // Group 3 — User & Session Info
    const allUserSessionChildren: SearchableMenuItem[] = [
      {
        id: 'userName',
        label: t('brain_logs.filters.user.user_name'),
        onClick: () => onFilterClick('userName'),
      },
      {
        id: 'userId',
        label: t('brain_logs.filters.user.user_id'),
        onClick: () => onFilterClick('userId'),
      },
      {
        id: 'userEmail',
        label: t('brain_logs.filters.user.user_email'),
        onClick: () => onFilterClick('userEmail'),
      },
      {
        id: 'externalUserId',
        label: t('brain_logs.filters.user.external_user_id'),
        onClick: () => onFilterClick('externalUserId'),
      },
      {
        id: 'channelUserId',
        label: t('brain_logs.filters.user.channel_user_id'),
        onClick: () => onFilterClick('channelUserId'),
      },
      {
        id: 'sessionId',
        label: t('brain_logs.filters.user.conversation_id'),
        onClick: () => onFilterClick('sessionId'),
      },
    ];

    const filteredUserSessionChildren = allUserSessionChildren.filter((item) =>
      isFilterAllowed(item.id)
    );

    const userSessionItems: SearchableMenuItem[] =
      filteredUserSessionChildren.length > 0
        ? [
            {
              id: 'user-session-info',
              label: t('brain_logs.filters.groups.user_session_info'),
              icon: <UserRoundIcon className="h-4 w-4 text-foreground-muted" />,
              children: filteredUserSessionChildren,
            },
          ]
        : [];

    // Group 4 — Additional
    const allAdditionalItems: SearchableMenuItem[] = [
      {
        id: 'hasRecording',
        label: t('brain_logs.filters.groups.voice_recordings'),
        icon: <FilterIcon type="hasRecording" />,
        onClick: () => onFilterClick('hasRecording'),
      },
      {
        id: 'hasVoicemail',
        label: t('brain_logs.filters.groups.voicemail'),
        icon: <FilterIcon type="hasVoicemail" />,
        onClick: () => onFilterClick('hasVoicemail'),
      },
      {
        id: 'isTest',
        label: t('brain_logs.filters.groups.preview_conversations'),
        icon: <FilterIcon type="isTest" />,
        onClick: () => onFilterClick('isTest'),
      },
    ];

    const additionalItems = allAdditionalItems.filter((item) => isFilterAllowed(item.id));

    const groups: SearchableMenuGroup[] = [];

    if (savedViewMenuItems.length > 0) {
      groups.push({
        id: 'saved-views-group',
        items: [
          {
            id: 'saved-views',
            label: t('brain_logs.saved_filter_views'),
            icon: <BookmarkIcon className="h-4 w-4 text-foreground-muted" />,
            children: savedViewMenuItems,
          },
        ],
      });
    }

    // Always add the first group
    groups.push({ id: 'filters-group-1', separator: true, items: group1Items });

    // Combine Metrics and User & Session Info under the same category
    const combinedMetricsItems = [...metricsItems, ...userSessionItems];
    if (combinedMetricsItems.length > 0) {
      groups.push({ id: 'metrics-group', separator: true, items: combinedMetricsItems });
    }

    // Only add additional group if it has items
    if (additionalItems.length > 0) {
      groups.push({ id: 'additional-group', separator: true, items: additionalItems });
    }

    return groups;
  }, [
    channelMenuItems,
    desks,
    brains,
    members,
    onFilterClick,
    savedViewMenuItems,
    t,
    allowedFilters,
  ]);

  return { menuGroups };
};
